import { useState, useRef, useEffect } from 'react';
import {
  MoreVertical,
  Pencil,
  Copy,
  Trash2,
  FolderOpen,
  Clock,
  Lock,
  Users,
} from 'lucide-react';
import type { Project } from '../../types';
import { useAuthStore } from '../../stores/authStore';

interface ProjectCardProps {
  project: Project;
  onOpen: (project: Project) => void;
  onRename: (id: string, newName: string) => Promise<void>;
  onDuplicate: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

function formatRelativeTime(dateStr: string) {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function ProjectCard({
  project,
  onOpen,
  onRename,
  onDuplicate,
  onDelete,
}: ProjectCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(project.name);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuPanelRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const canEditProject = useAuthStore((s) => s.canEditProject);
  const canDeleteProject = useAuthStore((s) => s.canDeleteProject);
  const isOwnerOf = useAuthStore((s) => s.isOwnerOf);
  const canEdit = canEditProject(project);
  const canDelete = canDeleteProject(project);
  const isOwner = isOwnerOf(project);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (menuPanelRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    const handleReposition = () => setMenuOpen(false);
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [menuOpen]);

  const openMenu = () => {
    const rect = menuButtonRef.current?.getBoundingClientRect();
    if (rect) {
      const menuWidth = 160;
      setMenuPos({
        top: rect.bottom + 4,
        left: Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8),
      });
    }
    setMenuOpen(true);
  };

  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [isRenaming]);

  const handleRename = async () => {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === project.name) {
      setRenameValue(project.name);
      setIsRenaming(false);
      return;
    }
    try {
      await onRename(project.id, trimmed);
      setIsRenaming(false);
    } catch {
      setRenameValue(project.name);
      setIsRenaming(false);
    }
  };

  const handleDuplicate = async () => {
    setMenuOpen(false);
    setIsDuplicating(true);
    try {
      await onDuplicate(project.id);
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleDelete = async () => {
    setMenuOpen(false);
    if (!window.confirm(`Delete "${project.name}"? This cannot be undone.`)) return;
    await onDelete(project.id);
  };

  const statusStyle =
    project.status === 'published'
      ? 'bg-emerald-500/10 text-emerald-400'
      : project.status === 'in_progress'
        ? 'bg-amber-500/10 text-amber-400'
        : project.status === 'review'
          ? 'bg-blue-500/10 text-blue-400'
          : project.status === 'approved'
            ? 'bg-emerald-500/10 text-emerald-400'
            : 'bg-zinc-500/10 text-zinc-500';

  return (
    <div
      className={`group relative bg-zinc-900/50 border border-white/[0.06] rounded-xl overflow-hidden
        hover:border-cyan-500/20 hover:bg-zinc-900 transition-all duration-200
        ${isDuplicating ? 'opacity-50 pointer-events-none' : ''}`}
    >
      <button
        onClick={() => onOpen(project)}
        className="w-full text-left"
      >
        <div className="relative aspect-video bg-zinc-800/50 border-b border-white/[0.04] flex items-center justify-center overflow-hidden">
          {project.thumbnail_url ? (
            <img
              key={project.thumbnail_url}
              src={project.thumbnail_url}
              alt={project.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <FolderOpen className="w-8 h-8 text-zinc-700" />
          )}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
            <span className="text-white/0 group-hover:text-white/70 text-xs font-medium transition-colors">
              Open
            </span>
          </div>
        </div>
      </button>

      <div className="p-3">
        {isRenaming ? (
          <input
            ref={renameInputRef}
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value.slice(0, 100))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleRename();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                setRenameValue(project.name);
                setIsRenaming(false);
              }
              e.stopPropagation();
            }}
            onBlur={handleRename}
            className="w-full bg-white/10 border border-cyan-400/50 rounded px-2 py-0.5 text-xs text-zinc-200
              outline-none focus:ring-1 focus:ring-cyan-400/50"
          />
        ) : (
          <div className="flex items-center justify-between gap-2">
            <h3
              className="text-xs font-medium text-zinc-300 group-hover:text-zinc-100 transition-colors truncate cursor-pointer"
              onClick={() => onOpen(project)}
              title={project.name}
            >
              {project.name || 'Untitled Project'}
            </h3>

            <div ref={menuRef} className="relative flex-shrink-0">
              <button
                ref={menuButtonRef}
                onClick={(e) => {
                  e.stopPropagation();
                  if (menuOpen) {
                    setMenuOpen(false);
                  } else {
                    openMenu();
                  }
                }}
                className="p-1 rounded hover:bg-white/10 text-zinc-600 hover:text-zinc-300
                  opacity-0 group-hover:opacity-100 transition-all"
              >
                <MoreVertical className="w-3.5 h-3.5" />
              </button>

              {menuOpen && menuPos && (
                <div
                  ref={menuPanelRef}
                  style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, width: 160 }}
                  className="bg-[#1e1e2e] border border-white/[0.12]
                  rounded-lg shadow-xl shadow-black/40 py-1 z-[100]">
                  {canEdit && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpen(false);
                        setIsRenaming(true);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-300
                        hover:bg-white/[0.06] hover:text-zinc-100 transition-colors"
                    >
                      <Pencil className="w-3 h-3" />
                      Rename
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDuplicate();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-300
                      hover:bg-white/[0.06] hover:text-zinc-100 transition-colors"
                  >
                    <Copy className="w-3 h-3" />
                    Duplicate
                  </button>
                  {canDelete && (
                    <>
                      <div className="my-1 border-t border-white/[0.06]" />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete();
                        }}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-400
                          hover:bg-red-500/10 hover:text-red-300 transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 mt-1.5">
          <span
            className={`text-[9px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wider ${statusStyle}`}
          >
            {project.status?.replace('_', ' ')}
          </span>
          <span className="flex items-center gap-1 text-[10px] text-zinc-600">
            <Clock className="w-2.5 h-2.5" />
            {formatRelativeTime(project.updated_at)}
          </span>
          {!isOwner && (
            <span
              className="flex items-center gap-1 text-[10px] text-zinc-500"
              title={canEdit ? 'Shared with you (editor)' : 'Shared with you (viewer)'}
            >
              {canEdit ? <Users className="w-2.5 h-2.5" /> : <Lock className="w-2.5 h-2.5" />}
              {canEdit ? 'Shared' : 'View only'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
